import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Contract } from '@console/utils';
import {
  contractsApi,
  CreateContractDto,
  ContractFilters,
  UpdateContractDto,
  RenewContractDto,
} from '@/lib/api/contracts';
import { movideskApi } from '@/lib/api/movidesk';
import { refreshSyncDates } from '@/lib/sync';

export function useContracts(filters?: ContractFilters) {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
  });

  const fetchContracts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await contractsApi.getAll(filters);
      setContracts(response.data);
      setPagination({
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('contracts.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [
    filters?.status,
    filters?.page,
    filters?.limit,
    filters?.sortBy,
    filters?.hideInactiveExpired,
    filters?.companyId,
    t,
  ]);

  const createContract = useCallback(
    async (contractData: CreateContractDto) => {
      try {
        setIsSaving(true);

        const newContract = await contractsApi.create(contractData);

        setContracts((prev) => [newContract, ...prev]);
        toast.success(t('contracts.createSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('contracts.createError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const updateContract = useCallback(
    async (id: string, contractData: UpdateContractDto) => {
      try {
        setIsSaving(true);
        const updatedContract = await contractsApi.update(id, contractData);
        // Update the contract in the current list
        setContracts((prev) =>
          prev.map((contract) => (contract.id === id ? updatedContract : contract))
        );
        toast.success(t('contracts.updateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('contracts.updateError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const deleteContract = useCallback(
    async (id: string) => {
      try {
        setIsSaving(true);
        await contractsApi.delete(id);
        // Remove the contract from the current list
        setContracts((prev) => prev.filter((contract) => contract.id !== id));
        toast.success(t('contracts.deleteSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('contracts.deleteError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const renewContract = useCallback(
    async (id: string, contractData: RenewContractDto) => {
      try {
        setIsSaving(true);
        const newContract = await contractsApi.renew(id, contractData);

        // Update the original contract (mark as inactive) and add the new one
        setContracts((prev) =>
          prev
            .map((contract) => (contract.id === id ? { ...contract, isActive: false } : contract))
            .concat(newContract)
        );

        toast.success(t('contracts.renewSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('contracts.renewError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const syncContracts = useCallback(async () => {
    try {
      setIsSyncing(true);
      const response = await movideskApi.syncContracts();
      toast.success(t('contracts.syncSuccess'));
      refreshSyncDates();
      return response.dataCreated;
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('contracts.syncError'));
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Filter active contracts (useful for selects where you don't want inactive/expired contracts)
  const activeContracts = useMemo(() => {
    return contracts.filter(
      (contract) => contract.isActive && !['EXPIRED', 'INACTIVE'].includes(contract.status)
    );
  }, [contracts]);

  return {
    contracts,
    activeContracts,
    isLoading,
    isSaving,
    isSyncing,
    pagination,
    createContract,
    updateContract,
    deleteContract,
    renewContract,
    syncContracts,
    refreshContracts: fetchContracts,
  };
}
